import { useMemo } from 'react'

import { ChainId } from '@src/configs/chainInfo'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'
import { useTokensStore } from '@src/zustand_stores'

export const filterTokens = (
    availableTokens: IToken[],
    chainId: string | number,
    isBuySide = false,
    sellData: IExchangeData,
    buyData: IExchangeData
) => {
    // Convert the chainId to a number for strict comparison
    const selectedChainId = Number(chainId)

    // First filter by chain ID - ensure strict number comparison
    let availableTokensTemp = availableTokens.filter(token => {
        const tokenChainId = Number(token.chainId)
        return tokenChainId === selectedChainId
    })

    // Filter out Bitcoin tokens if ENABLE_FEATHER is disabled
    if (!isFeatureEnabled('ENABLE_FEATHER')) {
        availableTokensTemp = availableTokensTemp.filter(token => token.chainId !== ChainId.Bitcoin)
    }

    // In testnet mode, enforce uniqueness based on chain ID and address
    if (isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')) {
        const uniqueMap = new Map<string, IToken>()
        availableTokensTemp.forEach(token => {
            const key = `${token.chainId}-${token.address.toLowerCase()}`
            uniqueMap.set(key, token)
        })
        availableTokensTemp = Array.from(uniqueMap.values())
    }

    // FE-382: Filter out same token on same chain
    const availableTokensExcludingSame = availableTokensTemp.filter(token => {
        if (isBuySide) {
            return token.chainId === sellData.chainId ? token.address !== sellData.token.address : true
        } else {
            return token.chainId === buyData.chainId ? token.address !== buyData.token.address : true
        }
    })

    // Sort tokens by market cap
    return availableTokensExcludingSame.sort((a, b) => {
        const aCap = a.market_cap ?? -Infinity
        const bCap = b.market_cap ?? -Infinity
        return bCap - aCap
    })
}

export const useAvailableTokens = (chainId: string | number, isSolanaSwap: boolean, isBuySide = false) => {
    const { tokens: availableTokens } = useTokensStore()
    const { sellData, buyData } = useExchangeDataContext()

    const tokens = useMemo(() => {
        if (!chainId) return []
        return filterTokens(availableTokens, chainId, isBuySide, sellData, buyData)
    }, [chainId, availableTokens, isSolanaSwap, isBuySide, sellData, buyData])

    return { tokens }
}
